import {
    AangevraagdeVestigingDto,
    ActieDto,
    BerichtDto,
    BetrokkenTreinDto,
    ContactpersoonDto,
    DossierDetailsDto,
    DossierOverzichtItemDto,
    LogboekEntryDto,
    OngewensteGebeurtenisDto,
    OpstartplanDto,
    SuggestieDto,
    TvvBesteldeRouteDto,
    VsmDto,
} from "@/shared/types";
import { ReactNode } from "react";

type EventType =
    | "DossierEvent"
    | "DossierOverzichtEvent"
    | "DossierDetailsEvent"
    | "BetrokkenStationsEvent"
    | "BetrokkenTreinEvent"
    | "ActieEvent"
    | "SuggestieEvent"
    | "LogboekEvent"
    | "BerichtEvent"
    | "ContactpersoonEvent"
    | "OngewensteGebeurtenisEvent"
    | "OpstartplanEvent"
    | "VestigingEvent"
    | "LandelijkeFaciliteitenEvent"
    | "VsmEvent"
    | "TvvEvent"
    | "MedewerkersOpStationEvent"
    | "DossierSettingsEvent"
    | "HerinneringEvent";

export type EventReason = "Nieuw" | "Wijziging" | "Verwijderd";

interface BaseNotificationEvent<Type extends EventType> {
    id: string;
    type: Type;
    podId: string;
    timestamp: number;
    reason: EventReason;
}

export type DossierOverzichtEvent = BaseNotificationEvent<"DossierOverzichtEvent"> & {
    dossierId: number;
    dossier: DossierOverzichtItemDto;
};

export type DossierDetailsEvent = BaseNotificationEvent<"DossierDetailsEvent"> & {
    dossierId: number;
    details: DossierDetailsDto;
};

export type BetrokkenStationEvent = BaseNotificationEvent<"BetrokkenStationsEvent"> & {
    dossierId: number;
};

export type BetrokkenTreinEvent = BaseNotificationEvent<"BetrokkenTreinEvent"> & {
    dossierId: number;
    betrokkenTrein: BetrokkenTreinDto;
};

export type BerichtEvent = BaseNotificationEvent<"BerichtEvent"> & {
    dossierId: number;
    bericht: BerichtDto;
};

export type ContactpersoonEvent = BaseNotificationEvent<"ContactpersoonEvent"> & {
    dossierId: number;
    contactpersoon: ContactpersoonDto;
};

export type ActieEvent = BaseNotificationEvent<"ActieEvent"> & {
    dossierId: number;
    actie: ActieDto;
};

export type SuggestieEvent = BaseNotificationEvent<"SuggestieEvent"> & {
    dossierId: number;
    suggestie: SuggestieDto;
};

export type LogboekEvent = BaseNotificationEvent<"LogboekEvent"> & {
    dossierId: number;
    logEntry: LogboekEntryDto;
};

export type OngewensteGebeurtenisEvent = BaseNotificationEvent<"OngewensteGebeurtenisEvent"> & {
    ongewensteGebeurtenis: OngewensteGebeurtenisDto;
};

export type OpstartplanEvent = BaseNotificationEvent<"OpstartplanEvent"> & {
    dossierId: number;
    opstartplan: OpstartplanDto;
};

export type VestigingEvent = BaseNotificationEvent<"VestigingEvent"> & {
    dossierId: number;
    aangevraagdeVestiging: AangevraagdeVestigingDto;
};

export type LandelijkeFaciliteitenEvent = BaseNotificationEvent<"LandelijkeFaciliteitenEvent"> & {
    dossierId: number;
};

export type VsmEvent = BaseNotificationEvent<"VsmEvent"> & {
    dossierId: number;
    vsm: VsmDto;
};

export type TvvEvent = BaseNotificationEvent<"TvvEvent"> & {
    dossierId: number;
    routes: TvvBesteldeRouteDto[];
};

export type MedewerkersOpStationEvent = BaseNotificationEvent<"MedewerkersOpStationEvent"> & {
    dossierId: number;
};

export type DossierSettingsEvent = BaseNotificationEvent<"DossierSettingsEvent"> & {
    dossierId: number;
};

export type HerinneringEvent = BaseNotificationEvent<"HerinneringEvent"> & {
    dossierId: number;
};

export type Event =
    | DossierOverzichtEvent
    | DossierDetailsEvent
    | BetrokkenStationEvent
    | BetrokkenTreinEvent
    | ActieEvent
    | SuggestieEvent
    | LogboekEvent
    | BerichtEvent
    | ContactpersoonEvent
    | OngewensteGebeurtenisEvent
    | OpstartplanEvent
    | VestigingEvent
    | LandelijkeFaciliteitenEvent
    | VsmEvent
    | TvvEvent
    | MedewerkersOpStationEvent
    | DossierSettingsEvent
    | HerinneringEvent;

/**
 * Connection status for RSocket
 */
export enum ConnectionStatus {
    CONNECTING = "CONNECTING",
    CONNECTED = "CONNECTED",
    DISCONNECTED = "DISCONNECTED",
    ERROR = "ERROR",
}

/**
 * RSocket topic subscription
 */
export interface TopicSubscription {
    isSubscribed: () => boolean;
    cancel: () => void;
}

/**
 * Topic message handler
 */
export type TopicMessageHandler<T> = (message: T) => void;

/**
 * RSocket context state
 */
export interface RSocketState {
    isConnected: boolean;
    status: ConnectionStatus;
    lastError: Error | null;
    messagesReceived: number;
    connect: () => Promise<void>;
    disconnect: () => void;
    subscribeTopic: <T extends Event>(topicName: string, onMessage: TopicMessageHandler<T>) => Promise<TopicSubscription>;
    getCurrentSubscriptions: () => string[];
}

/**
 * RSocket context provider props
 */
export interface RSocketProviderProps {
    children: ReactNode;
    url?: string;
    enabled?: boolean;
}

/**
 * Topic subscription options including onMessage handler and requestN
 */
export interface TopicSubscriptionOptions<T extends Event> {
    onMessage: TopicMessageHandler<T>;
    requestN?: number;
}
